import { z } from "zod";
import { brandSampleQuery } from "../brands/brands.schemas.js";
export const locationQuery = brandSampleQuery
    .innerType()
    .extend({
    group: z.enum(["fixture", "shelf"]).default("fixture"),
    fixtureId: z.string().uuid().optional(),
    brand: z.string().trim().min(1).max(255).optional(),
    categoryId: z.string().uuid().optional(),
    search: z.string().trim().max(120).default(""),
    page: z.coerce.number().int().min(1).max(10000).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
})
    .superRefine((query, ctx) => {
    const result = brandSampleQuery.safeParse(query);
    if (!result.success)
        for (const issue of result.error.issues)
            ctx.addIssue(issue);
});
const count = z.number().int().nonnegative();
const ratio = z.object({
    numerator: count,
    denominator: count,
    value: z.number().min(0).max(100).nullable(),
});
export const locationRow = z.object({
    id: z.string().uuid(),
    name: z.string(),
    storeId: z.string().uuid(),
    storeName: z.string(),
    fixtureId: z.string().uuid(),
    fixtureName: z.string(),
    level: z.number().int().nullable(),
    isCurrent: z.boolean(),
    configuredShelves: count,
    mappedShelves: count,
    imageCount: count,
    matchingImageCount: count,
    lastObservedAt: z.string().datetime().nullable(),
    evidenceCaptureId: z.string().uuid().nullable(),
    observedFacings: count,
    facings: count,
    productCount: count,
    brandCount: count,
    categoryCount: count,
    frontFacings: count,
    unknownOrientationFacings: count,
    observedShare: ratio,
    frontFacingShare: ratio,
});
export const locationResponse = z.object({
    definitionVersion: z.literal("location-image-observations/v1"),
    from: z.string(),
    to: z.string(),
    group: z.enum(["fixture", "shelf"]),
    computedAt: z.string().datetime(),
    fixtureName: z.string().nullable(),
    summary: z.object({
        captures: count,
        analyzedCaptures: count,
        fixtureLinkedCaptures: count,
        shelfLinkedCaptures: count,
        unresolvedCaptures: count,
        invalidLocationCaptures: count,
        configuredFixtures: count,
        observedCurrentFixtures: count,
        configuredShelves: count,
        observedCurrentShelves: count,
        mappedSections: count,
        detectedSections: count,
        observedFacings: count,
        unassignedShelfFacings: count.nullable(),
        fixtureCoverage: ratio,
        shelfMappingCoverage: ratio,
    }),
    items: z.array(locationRow).max(50),
    total: count,
    page: count,
    pageSize: count,
});
export const locationMapQuery = z
    .object({
    from: z.string(),
    to: z.string(),
    storeId: z.string().uuid(),
    mapId: z.string().uuid(),
    brand: z.string().trim().min(1).max(255).optional(),
})
    .superRefine((query, ctx) => {
    const result = brandSampleQuery.safeParse(query);
    if (!result.success)
        for (const issue of result.error.issues)
            ctx.addIssue(issue);
});
export const mapFixture = z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    position: z
        .object({ x: z.number().finite(), y: z.number().finite(), rotation: z.number().finite() })
        .nullable(),
    capturedImages: count,
    analyzedImages: count,
    observedFacings: count,
    selectedFacings: count,
    selectedImages: count,
    productCount: count,
    configuredShelves: count,
    mappedShelves: count,
    latestCaptureId: z.string().uuid().nullable(),
    latestCapturedAt: z.string().datetime().nullable(),
    latestAnalyzedCaptureId: z.string().uuid().nullable(),
    lastAnalyzedAt: z.string().datetime().nullable(),
    latestAnalyzedImageHash: z.string().nullable(),
});
export const locationMapResponse = z.object({
    definitionVersion: z.literal("fixture-map-observations/v1"),
    from: z.string(),
    to: z.string(),
    storeId: z.string().uuid(),
    mapId: z.string().uuid(),
    computedAt: z.string().datetime(),
    brand: z.string().nullable(),
    fixtures: z.array(mapFixture),
    brands: z.array(z.string()),
    unresolvedImages: count,
});
export const fixtureScansQuery = z
    .object({
    from: z.string(),
    to: z.string(),
    fixtureId: z.string().uuid(),
    period: z.enum(["selected", "all"]).default("selected"),
    page: z.coerce.number().int().min(1).max(10000).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(8),
})
    .superRefine((query, ctx) => {
    const result = brandSampleQuery.safeParse(query);
    if (!result.success)
        for (const issue of result.error.issues)
            ctx.addIssue(issue);
});
export const fixtureScan = z.object({
    id: z.string().uuid(),
    capturedAt: z.string().datetime(),
    analyzed: z.boolean(),
    imageHash: z.string().nullable(),
    observedFacings: count,
    mappedShelves: count,
});
export const fixtureScansResponse = z.object({
    items: z.array(fixtureScan).max(50),
    total: count,
    page: count,
    pageSize: count,
});
//# sourceMappingURL=location-analytics.schemas.js.map